/**
 * ESTABLISHEMNT-HOME
 * Home for the different journeys
 */

import PageLoader from "@/components/page-loader"
import PageHeader from "@/components/page-header"
import ChipButton from "@/components/chip-button"
import Link from "@/components/link"
import { sessionEdit } from "@/redux/_session.actions"
import { STATUS_SAVING } from "@/redux/_status.types"
import Modal from "@/components/modal"
import { useEffect, useState } from "react"
import HomeButton from "@/components/HomeButton"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { useLocation, useNavigate } from "react-router-dom"
import establishmentStore from "../model/establishment.store"
import { useEstablishmentNotifications } from "../hooks/useEstablishmentNotifications"
import { userFetch, userGet } from "@/redux/user.actions"
import EstablishmentNotificationsPanel from "./EstablishmentNotificationsPanel"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import siteStore from "@/entities/site/model/site.store"
import mapStore from "@/entities/map/model/map.store"
import populationStore from "@/entities/population/model/population.store"
import {
  faClipboardCheck,
  faFileAlt,
  faHeart,
  faTriangleExclamation,
  faPlus,
  faWandMagicSparkles
} from "@fortawesome/free-solid-svg-icons"
import { PhysicalRisk } from "@/entities/physical-risk/model/physical-risk.types"
import PhysicalRiskEditModal from "@/entities/physical-risk/ui/modal/PhysicalRiskEditModal"
import EstablishmentOverviewCard from "./EstablishmentOverviewCard"
import useOpenRiskCatalog from "@/entities/physical-risk/hooks/useOpenRiskCatalog"
import EstablishmentPreventionOverview from "@/entities/establishment/ui/EstablishmentPreventionOverview"
import ModalConfirm from "@/components/modal-confirm"
import EstablishmentOverviewEditModal from "./modal/EstablishmentOverviewEditModal"

export default function EstablishmentHome() {
  const navigate = useNavigate()
  const openRiskCatalog = useOpenRiskCatalog()
  const location = useLocation()
  const dispatch = useAppDispatch()

  const establishment = useAppSelector(establishmentStore.selectors.active)
  const status = useAppSelector(establishmentStore.selectors.status)
  const physicalRisks = useAppSelector(physicalRiskStore.selectors.list)
  //La visite terrain se fait sur les plans : sans plan importe, rien a visiter
  const hasMaps = useAppSelector(mapStore.selectors.list).length > 0
  const [modal, setModal] = useState<
    null | "MODAL_RISK_ADD" | "MODAL_OVERVIEW_CONFIRM" | "MODAL_OVERVIEW_EDIT" | "MODAL_REVIEW"
  >(null)
  const [isStartingScan, setIsStartingScan] = useState(false)
  const [editingRisk, setEditingRisk] = useState<PhysicalRisk | null>(null)

  const hasSideTasks = useEstablishmentNotifications().notifications.length > 0

  async function startScan() {
    setIsStartingScan(true)
    try {
      const response = await dispatch(establishmentStore.thunks.scan()).unwrap()
      if (!response.error) setModal(null)
    } finally {
      setIsStartingScan(false)
    }
  }

  useEffect(() => {
    dispatch(physicalRiskStore.actions.search({ value: null, columns: [] }))
    dispatch(siteStore.actions.search({ value: null, columns: [] }))
    dispatch(populationStore.actions.search({ value: null, columns: [] }))
  }, [dispatch])

  useEffect(() => {
    load()
  }, [establishment.id, location.search])

  useEffect(() => {
    if (modal !== "MODAL_REVIEW") return
    let cancelled = false
    async function refreshEstablishment() {
      const establishments = await establishmentStore.endpoints.fetch()
      if (cancelled || !Array.isArray(establishments)) return
      const updated = establishments.find((item) => item.id === establishment.id)
      if (updated) dispatch(establishmentStore.actions.replace(updated))
    }
    refreshEstablishment()
    return () => {
      cancelled = true
    }
  }, [modal, establishment.id, dispatch])

  async function load() {
    await dispatch(establishmentStore.thunks.fetchData())
    const users: any = await dispatch(userFetch("", null, null, [], ""))
    dispatch(userGet(users.error ? [] : users))
    const riskId = new URLSearchParams(location.search).get("riskId")
    if (riskId) {
      const risk = physicalRisks.find((item) => item.id === riskId)
      if (risk) setEditingRisk(risk)
    }
  }

  return (
    <div className="flex fixed top-[48px] left-0 h-[calc(100%-48px)] app-content-width">
      {modal === "MODAL_RISK_ADD" && (
        <PhysicalRiskEditModal onClose={() => setModal(null)} context={{ type: "generic" }} />
      )}
      {editingRisk && <PhysicalRiskEditModal risk={editingRisk} onClose={() => setEditingRisk(null)} />}
      {modal === "MODAL_OVERVIEW_CONFIRM" && (
        <ModalConfirm
          textBold="Souhaitez-vous modifier la vue d'ensemble ?"
          onNo={() => setModal(null)}
          onYes={() => setModal("MODAL_OVERVIEW_EDIT")}
        />
      )}
      {modal === "MODAL_OVERVIEW_EDIT" && <EstablishmentOverviewEditModal onClose={() => setModal(null)} />}
      {modal === "MODAL_REVIEW" && (
        <Modal
          title="Lancer une revue"
          isCloseButtonVisible
          onClose={() => setModal(null)}
          footerStart={
            establishment.scanResults?.length ? (
              <Link
                className="self-center whitespace-nowrap text-base"
                isWithoutMargin
                onClick={() => {
                  dispatch(sessionEdit("scanPanelEstablishmentId", establishment.id))
                  setModal(null)
                }}>
                Afficher les résultats précédents
              </Link>
            ) : undefined
          }
          onNext={startScan}
          onNextCta="Continuer"
          nextDisabled={isStartingScan}
          status={isStartingScan ? STATUS_SAVING : undefined}>
          <div className="w-[460px] mb-20">
            La revue génère un rapport avec des suggestions. Aucune modification n’est appliquée automatiquement.
          </div>
        </Modal>
      )}

      <EstablishmentNotificationsPanel />

      <div className="py-20 flex-1 flex flex-col overflow-auto px-[30px]">
        <PageHeader title={`Vue d’ensemble ${establishment.name}`}>
          <ChipButton icon={faWandMagicSparkles} onClick={() => setModal("MODAL_REVIEW")}>
            Lancer une revue
          </ChipButton>
        </PageHeader>

        <PageLoader status={status} onRefresh={() => load()} forceLoading isFlex className="flex-1 min-h-0">
          <div className="flex flex-1 min-h-0 flex-col gap-20 pb-20 -mt-10">
            <div className="flex min-w-[760px] gap-20">
              <HomeButton
                icon={faPlus}
                imageAsCta={hasSideTasks ? null : "owl-add-risks"}
                preTitle="Ajouter un"
                title="Risque"
                onClick={() => setModal("MODAL_RISK_ADD")}
              />
              <HomeButton
                icon={faTriangleExclamation}
                preTitle="Afficher les risques"
                title="Physiques"
                onClick={() => openRiskCatalog()}
              />
              <HomeButton icon={faHeart} preTitle="Afficher les" title="RPS" onClick={() => navigate("/risks/rps")} />
              <HomeButton
                icon={faFileAlt}
                preTitle="Consulter le"
                title="DUERP"
                onClick={() => navigate("/risks/documents?type=duerp")}
              />
              {hasMaps && (
                <HomeButton
                  icon={faClipboardCheck}
                  preTitle="Réaliser une"
                  title="Visite terrain"
                  onClick={() => {
                    dispatch(siteStore.actions.setActive(null))
                    navigate("/risks/sites")
                  }}
                />
              )}
            </div>

            <div className="establishment-dashboard-grid">
              <EstablishmentOverviewCard
                onAddClick={() => setModal("MODAL_OVERVIEW_EDIT")}
                onEditClick={() => setModal("MODAL_OVERVIEW_CONFIRM")}
              />
              <EstablishmentPreventionOverview />
            </div>
          </div>
        </PageLoader>
      </div>
    </div>
  )
}
